import { EmptyState, ErrorState, PageHeader, SegmentedControl, Skeleton } from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DetailsTable } from "@/components/team/DetailsTable";
import { MonthSwitcher } from "@/components/team/MonthSwitcher";
import { PersonPanel } from "@/components/team/PersonPanel";
import { PtoBanner } from "@/components/team/PtoBanner";
import { PtoRequestsPage } from "@/components/team/PtoRequestsPage";
import type { EntryFilter, PanelTab, PanelTarget } from "@/components/team/panel-state";
import { SignOffsPage } from "@/components/team/SignOffsPage";
import { SummaryTable } from "@/components/team/SummaryTable";
import { TagToggles } from "@/components/team/TagToggles";
import { TeamActions } from "@/components/team/TeamActions";
import { TeamTotalsBar } from "@/components/team/TeamTotalsBar";
import { TimeOffTimeline } from "@/components/team/TimeOffTimeline";
import { usePtoRequestList } from "@/hooks/use-pto-request-list";
import { useRememberedText } from "@/hooks/use-remembered-text";
import {
  useTeamMonth,
  useTeamPtoRequests,
  useTeamSignOffs,
  useTeamTimeOff,
} from "@/hooks/use-team";
import { type PtoTab, TEAM_TAG_FILTERS, type TeamTagFilter } from "@/lib/enums";
import { filterByTag } from "@/lib/team/details";
import { findTeamMember, personMetrics, teamTotals } from "@/lib/team/metrics";
import { monthFromParam, monthParam } from "@/lib/team/months";
import { approvableCount, defaultTab } from "@/lib/team/pto-list";
import { buildSignOffGrid } from "@/lib/team/signoff-grid";
import { type IsoDate, monthStartOf, todayInZone } from "@/lib/time/dates";
import type { TeamSearch } from "@/schemas/team";

const isTeamTag = (value: string): value is TeamTagFilter =>
  value === "" || TEAM_TAG_FILTERS.some((tag) => tag === value);

export function TeamScreen() {
  const params = useSearch({ from: "/dashboard/team" });
  const navigate = useNavigate({ from: "/dashboard/team" });
  const month: IsoDate = params.month
    ? monthFromParam(params.month)
    : monthStartOf(todayInZone(new Date()));
  // Every piece of page state an agent may want to come back to lives in the URL
  // (UI standard 12): month, view, the open dialogs and the person panel.
  const setSearch = (next: Partial<TeamSearch>, replace = false) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), replace });
  const setMonth = (m: IsoDate) => setSearch({ month: monthParam(m) });

  const view = params.view === "details" ? "details" : "summary";
  // One search across Summary and Details.
  const [peopleQuery, setPeopleQuery] = useRememberedText<string>("dashboard:team-search", "");
  const peopleSearch = { value: peopleQuery, onChange: setPeopleQuery };
  const setView = (v: "summary" | "details") =>
    setSearch({ view: v === "details" ? "details" : undefined });
  // The tag toggles carry across the Summary / Details switch.
  const [tag, setTag] = useRememberedText<TeamTagFilter>("dashboard:team-tag", "", isTeamTag);
  const signOffsOpen = params.signoffs === "open";
  const setSignOffsOpen = (open: boolean) =>
    setSearch({ signoffs: open ? "open" : undefined }, !open);
  // ?pto=open (also Home's Review): the dialog is up, on the To approve tab unless chosen.
  const ptoShown = params.pto === "open";
  const setPtoShown = (open: boolean) => setSearch({ pto: open ? "open" : undefined }, !open);
  const [ptoTab, setPtoTab] = useState<PtoTab>("pending");

  const team = useTeamMonth(monthParam(month));
  const signOffs = useTeamSignOffs(true);
  const pto = useTeamPtoRequests();
  // Held here, above the page, so held rows stay held across close and reopen.
  const ptoList = usePtoRequestList(pto.data?.rows, pto.dataUpdatedAt);
  // Started here, with the other page reads, so Time off is not a second round after the month:
  // the timeline (mounted once the month has members) reads the same query from the cache.
  useTeamTimeOff(monthParam(month));

  const people = useMemo(
    () => (team.data ? team.data.stats.map((r) => personMetrics(r, team.data.asOf)) : []),
    [team.data],
  );
  const totals = useMemo(() => teamTotals(people), [people]);
  const grid = useMemo(
    () =>
      signOffs.data
        ? buildSignOffGrid(
            signOffs.data.members,
            signOffs.data.rows,
            signOffs.data.today,
            new Date(signOffs.data.now),
          )
        : null,
    [signOffs.data],
  );

  const openPto = () => {
    setPtoTab(pto.data ? defaultTab(pto.data.rows) : "pending");
    setPtoShown(true);
  };
  const reviewPending = () => {
    setPtoTab("pending");
    setPtoShown(true);
  };
  // The ?person= link is matched ignoring case. Until the team has loaded there is no name to
  // show, so the panel waits; a person who is not on this team never opens it (the link is
  // cleared, quietly, once the team is known).
  const member = findTeamMember(people, params.person);
  const stranger = Boolean(params.person) && team.isSuccess && !member;
  useEffect(() => {
    if (stranger)
      void navigate({
        search: (prev) => ({ ...prev, person: undefined, panel: undefined, type: undefined }),
        replace: true,
      });
  }, [stranger, navigate]);
  const panel: PanelTarget | null = member
    ? {
        personId: member.personId,
        personName: member.personName,
        tab: params.panel ?? "summary",
        filter: params.type ?? "all",
      }
    : null;
  const openPerson = (personId: string, tab: PanelTab, filter: EntryFilter = "all") =>
    setSearch({
      person: personId,
      panel: tab === "summary" ? undefined : tab,
      type: filter === "all" ? undefined : filter,
    });
  const setPanel = (next: PanelTarget | null) =>
    next
      ? openPerson(next.personId, next.tab, next.filter)
      : setSearch({ person: undefined, panel: undefined, type: undefined }, true);

  const tagged = useMemo(() => filterByTag(people, tag), [people, tag]);
  const controls = (
    <>
      <SegmentedControl
        aria-label="View"
        value={view}
        onChange={(v) => setView(v === "details" ? "details" : "summary")}
        options={[
          { value: "summary", label: "Summary" },
          { value: "details", label: "Details" },
        ]}
      />
      <TagToggles value={tag} onChange={setTag} />
    </>
  );

  // Too wide for a dialog, so each is a page of its own at its old address (?signoffs=open, ?pto=open).
  if (signOffsOpen) {
    return (
      <SignOffsPage
        onClose={() => setSignOffsOpen(false)}
        grid={grid}
        isError={signOffs.isError}
        onRetry={() => void signOffs.refetch()}
      />
    );
  }
  if (ptoShown) {
    return (
      <PtoRequestsPage
        onClose={() => setPtoShown(false)}
        state={ptoList}
        tab={ptoTab}
        onTabChange={setPtoTab}
        view={pto.data}
        viewUpdatedAt={pto.dataUpdatedAt}
        isError={pto.isError}
        onRetry={() => void pto.refetch()}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader
        title="My Team"
        period={<MonthSwitcher month={month} onChange={setMonth} />}
        actions={
          <TeamActions
            overdueCount={grid?.overdueCount ?? 0}
            onSignOffs={() => setSignOffsOpen(true)}
            onPto={openPto}
          />
        }
      />
      <PtoBanner count={pto.data ? approvableCount(pto.data.rows) : 0} onReview={reviewPending} />

      {team.isError && (
        <ErrorState message="Team didn't load" onRetry={() => void team.refetch()} />
      )}
      {team.isPending && (
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      )}
      {team.data && team.data.members.length === 0 && (
        <EmptyState icon={Users} title="No direct reports" />
      )}

      {team.data && team.data.members.length > 0 && (
        <>
          <TeamTotalsBar totals={totals} />
          {view === "summary" ? (
            <SummaryTable
              people={tagged}
              onOpen={openPerson}
              controls={controls}
              search={peopleSearch}
            />
          ) : (
            <DetailsTable
              people={tagged}
              totals={totals}
              month={month}
              onOpen={openPerson}
              controls={controls}
              search={peopleSearch}
            />
          )}
          <TimeOffTimeline key={month} initialMonth={month} />
        </>
      )}

      <PersonPanel target={panel} month={month} onChange={setPanel} />
    </div>
  );
}
